import React, { useState, useEffect, useRef } from 'react';
import video from "../assests/Video Project 4.mp4"
import { 
  FaGem, 
  FaPalette, 
  FaHeartbeat, 
  FaLeaf,
  FaFish,
  FaTrophy,
  FaShieldAlt,
  FaSeedling
} from 'react-icons/fa';
import { 
  GiFishing, 
  GiFishbone, 
  GiStomach, 
  GiNoodles,
  GiHealthNormal,
  GiGrowth,
  GiVibratingShield,
  GiMinerals
} from 'react-icons/gi';
import { 
  MdPets, 
  MdOutlineHealthAndSafety, 
  MdOutlineColorLens,
} from 'react-icons/md';
import { 
  BiSolidColor, 
  BiHeart,
  BiDish
} from 'react-icons/bi';
import { 
  IoNutritionOutline, 
  IoFishOutline 
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const videoRef = useRef(null);
  const navigate =useNavigate()
  const features = [
  {
    title: "Premium Quality",
    description: "Made with the finest natural ingredients for optimal fish health",
    icon: <FaGem className="text-3xl" />,
    color: "#1770b6"
  },
  {
    title: "Vibrant Colors",
    description: "Enhances natural colors and promotes healthy growth",
    icon: <FaPalette className="text-3xl" />,
    color: "#ff6b6b"
  },
  {
    title: "Easy Digest",
    description: "Scientifically formulated for easy absorption and digestion",
    icon: <GiStomach className="text-3xl" />,
    color: "#4ecdc4"
  },
  {
    title: "Nutrient Rich",
    description: "Packed with essential vitamins and minerals",
    icon: <FaSeedling className="text-3xl" />,
    color: "#f9ca24"
  }
];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left
      setCurrentCard((prev) => (prev + 1) % features.length);
    }
    if (touchStart - touchEnd < -50) {
      // Swipe right
      setCurrentCard((prev) => (prev - 1 + features.length) % features.length);
    }
  };

  return (
    <section className="relative w-full overflow-hidden bg-gray-900">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ${
            (isVideoLoaded || hasVideoError) ? 'opacity-100' : 'opacity-0'
          }`}
          onLoadedData={() => setIsVideoLoaded(true)}
          onError={() => setHasVideoError(true)}
          poster="https://via.placeholder.com/1920x1080/1770b6/ffffff?text=Lion+Head"
        >
          <source src={video} type="video/mp4" />
        </video>
        
        {/* Dark Overlay - Different gradients for mobile/desktop */}
        <div className={`absolute inset-0 ${
          isMobile 
            ? 'bg-gradient-to-b from-black/50 via-black/50 to-black/50' 
            : 'bg-gradient-to-r from-black/50 via-black/40 to-black/50'
        }`} />
        
        {/* Additional Gradient Overlay for Better Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex items-start md:items-center justify-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-12 md:py-20">
          
          {/* DESKTOP VERSION */}
          {!isMobile && (
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text Content */}
              <div className="text-center lg:text-left animate-fade-in-up">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-6">
                  <span className="w-2 h-2 bg-[#1770b6] rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-white tracking-wide">
                    Premium Quality Fish Food
                  </span>
                </div>

                {/* Main Heading */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6">
                  Feed Your Fish
                  <span className="block text-white mt-2">The Lion Head Way</span>
                </h1>

                {/* Description */}
                <p className="text-base sm:text-lg text-gray-200 max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                  Scientifically formulated premium fish food for vibrant colors, 
                  healthy growth, and optimal nutrition. Trusted by aquarium enthusiasts worldwide.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <button
                  onClick={() => navigate('/fish-food')}
                  className="group relative bg-[#1770b6] text-white px-8 py-3 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-[#0e5a94] hover:shadow-lg hover:shadow-[#1770b6]/30 transform hover:-translate-y-0.5 overflow-hidden">
                    <span className="relative z-10">Shop Now</span>
                    <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  </button>
                  <button className="group border-2 border-white/30 backdrop-blur-sm text-white px-8 py-3 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-white/10 hover:border-white/50">
                    Learn More
                    <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>

                {/* Stats Section */}
                <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto lg:mx-0 mt-16 pt-8 border-t border-white/20">
                  <div className="text-center lg:text-left">
                    <div className="text-2xl lg:text-3xl font-bold text-white">10K+</div>
                    <div className="text-xs text-gray-300 mt-1">Happy Fish Owners</div>
                  </div>
                  <div className="text-center lg:text-left">
                    <div className="text-2xl lg:text-3xl font-bold text-white">50+</div>
                    <div className="text-xs text-gray-300 mt-1">Fish Species</div>
                  </div>
                  <div className="text-center lg:text-left">
                    <div className="text-2xl lg:text-3xl font-bold text-white">100%</div>
                    <div className="text-xs text-gray-300 mt-1">Natural Ingredients</div>
                  </div>
                </div>
              </div>

              {/* Right Column - Feature Cards */}
              <div className="grid grid-cols-1 gap-4 animate-fade-in-right">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="group bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20 hover:border-[#1770b6]/50 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-xl"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-3xl group-hover:scale-110 transition-transform duration-300">
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white text-lg mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-300 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MOBILE VERSION - App-like Experience */}
          {isMobile && (
            <div className="flex flex-col min-h-[calc(100vh-5rem)] justify-start">
              {/* Hero Text */}
              <div className="text-center mb-8 animate-fade-in-up">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-4">
                  <span className="w-1.5 h-1.5 bg-[#1770b6] rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-white tracking-wide">
                    LION HEAD PREMIUM
                  </span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-3 leading-tight">
                  Feed Your Fish
                  <span className="block text-white mt-1">The Best Way</span>
                </h1>
                <p className="text-gray-200 text-sm px-4 leading-relaxed">
                  Premium fish food for vibrant colors, healthy growth, and optimal nutrition
                </p>
              </div>

              {/* Swipeable Card Section - App-like */}
              <div className="mb-8 animate-fade-in-up animation-delay-200">
                <div 
                  className="relative"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl transform transition-all duration-300">
                    <div className="text-center">
                      <div className="text-6xl mb-4 animate-bounce-soft">
                        {features[currentCard].icon}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {features[currentCard].title}
                      </h3>
                      <p className="text-gray-200 text-sm leading-relaxed">
                        {features[currentCard].description}
                      </p>
                      
                      {/* Progress Bar */}
                      <div className="mt-6 h-1 bg-white/20 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#1770b6] rounded-full transition-all duration-300"
                          style={{ width: `${((currentCard + 1) / features.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Card Indicators */}
                  <div className="flex justify-center gap-2 mt-4">
                    {features.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentCard(index)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          currentCard === index 
                            ? 'w-6 bg-[#1770b6]' 
                            : 'w-1.5 bg-white/50'
                        }`}
                        aria-label={`Go to feature ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  {/* Swipe Hint */}
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <span className="text-white/40 text-xs">←</span>
                    <p className="text-white/50 text-xs font-medium">
                      Swipe to explore features
                    </p>
                    <span className="text-white/40 text-xs">→</span>
                  </div>
                </div>
              </div>

              {/* CTA Buttons - App-like Bottom Action */}
              <div className="space-y-3 animate-fade-in-up animation-delay-400">
                <button
                                  onClick={() => navigate('/fish-food')}

                className="w-full bg-[#1770b6] text-white py-3.5 rounded-full font-semibold shadow-lg active:scale-95 transition-transform duration-200 flex items-center justify-center gap-2">
                  <span>Shop Now</span>
                  <span className="text-lg">→</span>
                </button>
                <button className="w-full border border-white/30 text-white py-3.5 rounded-full font-semibold active:scale-95 transition-transform duration-200 backdrop-blur-sm">
                  Learn More
                </button>
              </div>

              {/* Quick Stats - Horizontal Scroll */}
              <div className="mt-8 overflow-x-auto scrollbar-hide animate-fade-in-up animation-delay-600">
                <div className="flex gap-4 px-1 pb-2">
                  <div className="flex-shrink-0 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-2 text-center min-w-[100px]">
                    <div className="text-lg font-bold text-white">10K+</div>
                    <div className="text-[10px] text-gray-400">Happy Owners</div>
                  </div>
                  <div className="flex-shrink-0 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-2 text-center min-w-[100px]">
                    <div className="text-lg font-bold text-white">50+</div>
                    <div className="text-[10px] text-gray-400">Fish Species</div>
                  </div>
                  <div className="flex-shrink-0 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-2 text-center min-w-[100px]">
                    <div className="text-lg font-bold text-white">100%</div>
                    <div className="text-[10px] text-gray-400">Natural</div>
                  </div>
                  <div className="flex-shrink-0 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-2 text-center min-w-[100px]">
                    <div className="text-lg font-bold text-white">24/7</div>
                    <div className="text-[10px] text-gray-400">Support</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator - Only on Desktop */}
      {!isMobile && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white rounded-full mt-2 animate-scroll" />
          </div>
        </div>
      )}
    </section>
  );
};


export default HeroSection

// Add these animations to your global CSS or tailwind.config.js
// If using Tailwind, add these to your config:

/*
Add to tailwind.config.js:

module.exports = {
  theme: {
    extend: {
      animation: {
        'fade-in-up': 'fadeInUp 0.6s ease-out',
        'fade-in-right': 'fadeInRight 0.6s ease-out',
        'bounce-soft': 'bounceSoft 2s infinite',
        'scroll': 'scroll 1.5s ease-in-out infinite',
      },
      keyframes: {
        fadeInUp: {
          '0%': {
            opacity: '0',
            transform: 'translateY(20px)'
          },
          '100%': {
            opacity: '1',
            transform: 'translateY(0)'
          },
        },
        fadeInRight: {
          '0%': {
            opacity: '0',
            transform: 'translateX(-20px)'
          },
          '100%': {
            opacity: '1',
            transform: 'translateX(0)'
          },
        },
        bounceSoft: {
          '0%, 100%': {
            transform: 'translateY(0)'
          },
          '50%': {
            transform: 'translateY(-5px)'
          },
        },
        scroll: {
          '0%': {
            transform: 'translateY(0)',
            opacity: '1'
          },
          '100%': {
            transform: 'translateY(8px)',
            opacity: '0'
          },
        },
      },
    },
  },
  plugins: [
    function({ addUtilities }) {
      addUtilities({
        '.scrollbar-hide': {
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        },
        '.animation-delay-200': {
          'animation-delay': '200ms',
        },
        '.animation-delay-400': {
          'animation-delay': '400ms',
        },
        '.animation-delay-600': {
          'animation-delay': '600ms',
        },
        '.animation-delay-800': {
          'animation-delay': '800ms',
        },
      })
    }
  ],
}
*/
